import React, { useCallback } from "react";

import classNames from "classnames";

import { useDispatch } from "react-redux";
import { closeCart, updateCartItem } from "@store/cart";
import { navigate } from "gatsby";

import CartItemProperty from "@components/widgets/cart/item/property/CartItemProperty";
import CartItemFooter from "@components/widgets/cart/item/footer/CartItemFooter";
import CartItemHeader from "@components/widgets/cart/item/title/CartItemHeader";
import CartItemImg from "@components/widgets/cart/item/img/CartItemImg";

import type { Product } from "@typings/product/product";
import type { CartItemType } from "@typings/cart";
import CartItemSize from "./CartItemSize";

interface CartItemProps {
  cartItem: Product;

  className?: string;

  type?: CartItemType;

  hideRemoveBtn?: boolean;
  hideCounter?: boolean;
  isTopDeal?: boolean;
  isComplete?: boolean;
}

const sizes = [
  {
    value: "M",
  },
  {
    value: "L",
  },
];

const getSelectedSizeName = (cartItem: Product): string => {
  const sizeOption = cartItem.item.options.find((opt) => opt.name === "Size");

  if (!sizeOption) return "";

  const selectedValueId = cartItem.configuration.options?.[sizeOption.id];
  const selectedValue = sizeOption.values.find((val) => val.id === selectedValueId);

  return selectedValue?.name || "";
};

const CartItem = ({
  cartItem,
  className,
  type = "EDIT",
  hideRemoveBtn,
  hideCounter,
  isTopDeal,
  isComplete,
}: CartItemProps) => {
  const { item, configuration } = cartItem;

  const dispatch = useDispatch();

  const isDisplaySize = [7, 8].includes(item.id);
  const isFreeGift = item.category.name === "Free Gift";

  const handleSizeChange = (newSizeName: string) => {
    const sizeOption = cartItem.item.options.find((opt) => opt.name === "Size");
    const newValue = sizeOption?.values.find((val) => val.name === newSizeName);

    if (!sizeOption || !newValue) return;

    const updatedConfiguration = {
      ...cartItem.configuration,
      options: {
        ...cartItem.configuration.options,
        [sizeOption.id]: newValue.id,
      },
    };

    const updatedCartItem = {
      ...cartItem,
      configuration: updatedConfiguration,
    };

    dispatch(updateCartItem({ oldItem: cartItem, newItem: updatedCartItem }));
  };

  const redirectToProductPage = useCallback(() => {
    if (item.price === 0) return;

    void navigate(`/shop/${item.information?.slug}`);

    dispatch(closeCart());
  }, [dispatch, item]);

  return (
    <React.Fragment>
      <div className={classNames("cart-item", className, { "free-item": isFreeGift })}>
        <CartItemImg item={item} redirectToProductPage={redirectToProductPage} />

        <div className="cart-item__info">
          <CartItemHeader cartItem={cartItem} redirectToProductPage={redirectToProductPage} />

          <CartItemProperty
            name={configuration.price !== 0 ? configuration.offer.title : undefined}
            price={configuration.price}
            value=""
          />

          {isDisplaySize && (
            <CartItemSize
              selectedSize={getSelectedSizeName(cartItem)}
              handleSizeChange={handleSizeChange}
              sizes={sizes}
              isComplete={isComplete}
            />
          )}

          <CartItemFooter
            cartItem={cartItem}
            type={type}
            hideRemoveBtn={hideRemoveBtn}
            hideCounter={hideCounter}
            isTopDeal={isTopDeal}
          />
        </div>
      </div>

      <hr className="cart-item__separator" />
    </React.Fragment>
  );
};

export default CartItem;
